import { useCallback, useEffect, useState } from "react";

/**
 * The Strategy Report's Beginning Concentration: 100% by definition, editable,
 * and back to 100% on every page load (operator, task 3141).
 *
 * The hook holds the BASELINE, not what the row displays: before any report
 * has run the row reads "No Report" rather than this 100 (operator, task
 * 4848), and that choice is beginningConcentrationFigure's, in the Strategy
 * Report's strategy/diversifyFigures.
 * The third element says whether the reader has typed over the baseline,
 * because a typed figure is theirs and shows whatever the report's state.
 *
 * It is a hook of its own rather than a `useStrategyReportValue` call or a
 * plain `useState` in the card, because BOTH of those get the lifetime wrong
 * and in opposite directions.
 *
 * Not `useStrategyReportValue`: the operator asked for a page load to return
 * to the definition, which a localStorage value never does.
 *
 * Not plain `useState` either. `StrategyReportContent` renders inside an
 * @mui/lab `TabPanel`, which renders nothing at all while the Stock Report tab
 * is selected, so the whole report UNMOUNTS on a tab switch -- and the report
 * page's window-level keydown toggles that tab on a bare Tab press from
 * anywhere, the concentration field included. Held in component state the
 * operator's 80% vanished the moment they tabbed out of the field and the row
 * reverted to 100 with no signal, which then reached the client's PDF as 100
 * (the PDF filter cannot tell a default from a typed figure). Every other row
 * survives that remount only because its hook re-reads localStorage; this one
 * has nowhere to read back from.
 *
 * Module scope is exactly the lifetime the answer asks for: it outlives the
 * remount, and a page load re-evaluates this module and so returns to 100.
 *
 * The override is also SCOPED TO ONE ACCOUNT (operator, task 3141), which is
 * the other half of the same answer: a baseline typed for one client is not a
 * claim about the next one, and it would otherwise reach that client's
 * exported PDF as their opening concentration. So `accountKey` identifies
 * whose card this is, and the override is dropped the moment it changes.
 *
 * That key must be stable across the remount above and live on a real account
 * change, which is why it comes from the SETTINGS STORE rather than from
 * `settingsInfo`: the settings context is created inside the same unmounting
 * panel and starts empty on every remount, so keying on it would read as an
 * account change on every tab switch and undo the whole point of the module
 * holder. `useSettingsReportValue` reads localStorage synchronously on mount
 * and listens for the picker's own write, so it is steady across the remount
 * and current the moment a client is actually switched.
 */
export const BEGINNING_CONCENTRATION_DEFAULT = 100;

let override: number | string | null = null;
let overrideAccountKey: string | null = null;

/** Test seam -- a page load is what clears these in the browser. */
export const resetBeginningConcentration = () => {
  override = null;
  overrideAccountKey = null;
};

export const useBeginningConcentration = (
  accountKey: string,
): [number | string, (value: number | string) => void, boolean] => {
  const [value, setValue] = useState<number | string>(
    override !== null && overrideAccountKey === accountKey
      ? override
      : BEGINNING_CONCENTRATION_DEFAULT,
  );
  const [overridden, setOverridden] = useState(
    override !== null && overrideAccountKey === accountKey,
  );

  // Runs on mount as well as on a change, and is idempotent, so it covers the
  // account being switched WHILE this component is mounted and the account
  // having been switched while it was not.
  useEffect(() => {
    if (override === null || overrideAccountKey === accountKey) return;
    override = null;
    overrideAccountKey = null;
    setValue(BEGINNING_CONCENTRATION_DEFAULT);
    setOverridden(false);
  }, [accountKey]);

  const set = useCallback(
    (next: number | string) => {
      override = next;
      overrideAccountKey = accountKey;
      setValue(next);
      setOverridden(true);
    },
    [accountKey],
  );

  return [value, set, overridden];
};
